import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/react';
import { type ChatMessageData, type FallbackChatFile } from '@/components/chat';
import { useChatUIStore, GENERAL_CHAT_MODELS } from '@/stores/chat-ui-store';
import { listArtifacts } from '@/services/artifacts/list-artifacts';
import type { ArtifactListItem } from '@/types/artifacts';
import {
  useClarificationPrefsStore,
  modeAllowsClarifications,
} from '@/stores/clarification-prefs-store';
import { createGeneralChatSession } from '@/services/workflow/create-general-chat-session';
import { hashGeneralChatSetContextPayload } from '@/stores/helpers/general-chat-utils';
import { useNotification } from '@/components/notifications';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import { useNodeStore, withSuppressedSnapshots } from '@/stores/node-store';
import { useOrgStore } from '@/stores/org-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { useReactFlow } from '@xyflow/react';
import { getStructDisplayLabel } from '@/stores/helpers/node-naming';
import { getFreshToken } from '@/utils/auth-token-provider';
import { accumulateStreamingText } from '@/utils/accumulate-streaming-text';
import {
  saveChatHistory,
  loadChatHistory,
} from '@/stores/helpers/chat-history-db';
import { buildMutationLabel as buildSharedMutationLabel } from '@/components/chat/helpers/mutation-label';
import { findCatalogStruct } from '@/stores/helpers/node-catalog-lookup';
import { useWorkflowMutationApplier } from '@/hooks/use-workflow-mutation-applier';

const WORKFLOW_WS_KEY = '__workflow__';
const SAVE_DEBOUNCE_MS = 300;

/** Signal payload passed when a chat-initiated run ends. */
export interface ChatRunEndedSignal {
  /** Monotonically incrementing counter so the effect fires on each signal. */
  seq: number;
  /** WorkflowRun.id used to filter artifacts by ``source_run_id``. */
  runId: string | null;
  /** Canvas terminal NodeFiles for fallback when promotion times out. */
  fallbackFiles: FallbackChatFile[];
}

/**
 * Force-resolve pending clarifications no future WS event can resolve, so the
 * card can't hang forever with a live input routing nowhere.
 *
 * Same-session pendings survive when currentSessionId is given; omitting it
 * treats every pending as cross-session. Returns the input array referentially
 * unchanged when nothing needs resolving, so callers can pass it to setState.
 */
export function sanitizeStalePendingClarifications(
  msgs: ChatMessageData[],
  currentSessionId?: string | null,
): ChatMessageData[] {
  let mutated = false;
  const next = msgs.map((m) => {
    if (
      m.variant !== 'clarification_request' ||
      !m.clarificationRequest ||
      m.clarificationResolution !== undefined
    ) {
      return m;
    }
    if (
      currentSessionId &&
      m.clarificationRequest.sessionId === currentSessionId
    ) {
      return m;
    }
    mutated = true;
    return { ...m, clarificationResolution: 'timed_out' as const };
  });
  return mutated ? next : msgs;
}

export function commitChatMessageUpdate(
  messagesRef: { current: ChatMessageData[] },
  setMessages: (messages: ChatMessageData[]) => void,
  updater: (messages: ChatMessageData[]) => ChatMessageData[],
): ChatMessageData[] {
  const updated = updater(messagesRef.current);
  messagesRef.current = updated;
  setMessages(updated);
  return updated;
}

function getEditingLockedWarning(isWorkflowLockActive: boolean): string {
  return isWorkflowLockActive
    ? 'Workflow is locked by an admin — ask them to unlock it to apply chat-proposed changes.'
    : 'Canvas editing is locked — unlock it to apply chat-proposed changes.';
}

export interface NodeContext {
  node_model?: string;
  system_prompt?: string;
  task?: string;
  node_display_name?: string;
  node_description?: string;
  extra_params?: Record<string, unknown>;
  available_fields?: string[];
}

export function useGeneralChat(
  selectedNodeId: string | null,
  workflowId?: string | null,
  runSessionId?: string | null,
  options?: {
    /**
     * Dispatch a run of the current workflow via the editor's Play-button
     * path. Resolves to the run's id when actually dispatched; null when
     * refused (already running / validation — the editor shows its own
     * toasts). This hook only needs to know whether a run started; it
     * follows the run through ``chatRunEndedSignal`` rather than by id.
     */
    onRunWorkflow?: () => Promise<string | null>;
    /**
     * Set by the editor when a chat-initiated run ends.
     * When ``seq`` increments, the hook polls for promoted artifacts
     * and inserts an artifact message.
     *
     * - ``runId``       — WorkflowRun.id used to query by ``source_run_id``.
     *   Null when the run failed before a session could be registered.
     * - ``fallbackFiles`` — canvas terminal NodeFiles to show if polling
     *   times out before promoted Artifacts appear.
     */
    chatRunEndedSignal?: ChatRunEndedSignal | null;
    /**
     * Called once the artifact signal for the given ``seq`` has been fully
     * consumed (artifacts added to chat OR fallback shown). The parent
     * should clear ``chatRunEndedSignal`` only if it still holds that same
     * ``seq`` — a newer signal may have already replaced it while this one
     * was polling — so a remounted hook instance doesn't re-process a
     * signal that was already handled, without dropping one that wasn't.
     */
    onChatRunSignalConsumed?: (seq: number) => void;
    /**
     * Whether the canvas padlock (session lock) is active. Combined with the
     * persisted workflow lock to form the full editing lock that is sent to
     * the backend and gates all Apply handlers.
     */
    isCanvasLocked?: boolean;
  },
) {
  const {
    handleParameterChange,
    updateNodeAttributes,
    onNodesEdgesDelete,
    saveSnapshot,
  } = useNodeStore();
  const { getNode } = useReactFlow();
  const notification = useNotification();
  const { setActiveTab: setConsoleSidebarActiveTab, setConsolePanelOpen } =
    useWorkflowPanelStore();
  const {
    selectedModel,
    isGeneralChatActive,
    setGeneralChatActive,
    setChatMode,
  } = useChatUIStore();
  // The chat-agent rail has no timeout, so ask_with_timeout and ask_and_wait
  // are equivalent here and only the derived "allow" bit matters. The
  // per-workflow override wins when active, matching the footer pill.
  const allowClarifications = useClarificationPrefsStore((s) =>
    modeAllowsClarifications(s.workflowSession?.mode ?? s.mode),
  );
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const orgId = currentOrg?.orgid ?? '';
  const groupedNodes = useNodeStore((s) => s.groupedNodes);

  // Lock state — read-only chat when the workflow is locked: tell the BE
  // via `is_locked` (it hides mutation tools + refuses mutation calls) and
  // disable Apply on proposal cards client-side.  Gated by the same
  // LaunchDarkly flag as the rest of the lock UI.
  const { isWorkflowLockingEnabled } = useLaunchDarkly();
  const currentWorkflowIsLocked = useWorkflowStore(
    (s) => s.currentWorkflow?.is_locked ?? false,
  );
  // Canvas padlock (session-only) and persisted workflow lock both gate chat
  // mutations. Canvas lock is not gated by the LD flag — it is always active
  // when set. Tracked separately (not just OR'd) so lock messaging can tell
  // the user which one to resolve, and how.
  const isCanvasLockActive = options?.isCanvasLocked ?? false;
  const isWorkflowLockActive =
    isWorkflowLockingEnabled && currentWorkflowIsLocked;
  const isEditingLocked = isCanvasLockActive || isWorkflowLockActive;
  // Used below to gate the lock-toggle effect on workflow-data hydration.
  // See `isLockStateHydrated` for the full rationale.
  const currentWorkflowIdFromStore = useWorkflowStore(
    (s) => s.currentWorkflow?.id ?? null,
  );

  const [chatMessages, setChatMessages] = useState<ChatMessageData[]>([]);
  const [isGeneralChatConnecting, setIsGeneralChatConnecting] = useState(false);
  const [isGeneralChatLoading, setIsGeneralChatLoading] = useState(false);
  const [isGeneralChatReady, setIsGeneralChatReady] = useState(false);

  // Single WS connection for the unified chat
  const workflowWsRef = useRef<WebSocket | null>(null);
  const lastContextHashRef = useRef<string>('');
  const generalChatGenRef = useRef(0);
  const nodeStreamingRef = useRef<Map<string, { id: string; content: string }>>(
    new Map(),
  );
  // Track which node was focused when we last sent set_context
  const lastFocusedNodeIdRef = useRef<string | null>(null);
  const focusDividerInitializedRef = useRef(false);

  // Effective workflowId for persistence (skip unsaved "new" workflows)
  const effectiveWorkflowId =
    workflowId && workflowId !== 'new' ? workflowId : null;
  const effectiveWorkflowIdRef = useRef(effectiveWorkflowId);
  effectiveWorkflowIdRef.current = effectiveWorkflowId;

  // True only when the store's ``currentWorkflow`` matches the workflow
  // being viewed.  On load/navigation it can briefly be null or stale, so
  // the lock-toggle effect skips until this is true -- otherwise opening a
  // locked workflow would append a spurious "now locked" divider.  Unsaved
  // workflows (no id) have nothing to hydrate, so treated as not hydrated.
  const isLockStateHydrated =
    effectiveWorkflowId !== null &&
    currentWorkflowIdFromStore === effectiveWorkflowId;

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const historyLoadedForRef = useRef<string | null>(null);

  // Refs to always hold the latest values so WS handlers avoid stale closures
  const selectedNodeIdRef = useRef(selectedNodeId);
  selectedNodeIdRef.current = selectedNodeId;
  const groupedNodesRef = useRef(groupedNodes);
  groupedNodesRef.current = groupedNodes;
  const handleWorkflowMutationEventRef = useRef<
    (data: Record<string, unknown>) => void
  >(() => {});
  const finalizeWorkflowProgressRef = useRef<() => void>(() => {});
  const pendingWorkflowMutationsRef = useRef<
    Array<{ label: string; eventData: Record<string, unknown> }>
  >([]);
  // Set when the assistant calls run_workflow; flushed into a run
  // card + auto-dispatched when the turn finalizes.
  const pendingRunRequestRef = useRef(false);
  const onRunWorkflowRef = useRef(options?.onRunWorkflow);
  onRunWorkflowRef.current = options?.onRunWorkflow;
  // Ref so finalizeWorkflowProgress (defined earlier) can auto-dispatch.
  // sameTurnMutations is passed explicitly — chatMessagesRef may not yet
  // reflect the mutations card appended in the same finalize batch.
  const dispatchChatRunRef = useRef<(sameTurnMutations: boolean) => void>(
    () => {},
  );
  // Tracks instance_id → display name for nodes created in the current
  // buffered batch (before they exist on the canvas).
  const pendingNodeNameMapRef = useRef<Map<string, string>>(new Map());
  // Keep chatMessages accessible via ref for callbacks that can't depend on state
  const chatMessagesRef = useRef(chatMessages);
  chatMessagesRef.current = chatMessages;
  // Index in chatMessagesRef.current where this page visit's live activity
  // with the current workflow begins — everything before it was restored
  // from IndexedDB the first time this visit loaded that workflow's history,
  // i.e. from a prior session. 0 until that load resolves, which is correct
  // when there's nothing to restore. Unlike a per-turn boundary, this
  // doesn't move on every user message, so a card from earlier in the same
  // live session (e.g. one an "apply changes" retry needs after an
  // editing-locked attempt) stays reachable.
  const liveSessionStartIndexRef = useRef(0);
  // IndexedDB is also the cache switching workflows reads/writes within a
  // single page visit, so only a workflow's first load THIS VISIT can be a
  // prior session — tracked per workflow id (not just hook-wide) so
  // switching to one not yet opened this visit still gets its restored
  // history treated as possibly stale, while re-opening one already visited
  // is trusted as this visit's own recently-saved state.
  const hydratedWorkflowIdsRef = useRef<Set<string>>(new Set());
  // The hydrate effect fires per workflow id, not per session, so it needs a
  // ref to read the session id current at cache-load time.
  const runSessionIdRef = useRef<string | null>(runSessionId ?? null);
  runSessionIdRef.current = runSessionId ?? null;

  // Helper to get display name for a node ID
  const getNodeDisplayName = useCallback(
    (nodeId: string | null): string | null => {
      if (!nodeId) return null;
      const node = getNode(nodeId);
      const struct = (
        node?.data as
          | {
              struct?: {
                user_config_name?: string;
                display_name?: string;
              };
            }
          | undefined
      )?.struct;
      return getStructDisplayLabel(struct) ?? nodeId;
    },
    [getNode],
  );

  const extractNodeContext = useCallback(
    (nodeId: string): NodeContext | undefined => {
      const node = getNode(nodeId);
      const nodeData = node?.data as
        | {
            struct?: {
              params?: Array<{
                field_id: string;
                value: unknown;
                display_name: string;
                display_modal?: string;
              }>;
              user_config_name?: string;
              user_config_description?: string;
              display_name?: string;
            };
          }
        | undefined;
      const struct = nodeData?.struct;
      if (!struct?.params) return undefined;

      const SYSTEM_PROMPT_IDS = ['system_prompt'];
      const TASK_IDS = ['user_prompt', 'task', 'request'];
      const MODEL_IDS = ['model'];
      const SKIP_IDS = ['name', 'files', 'mcp_servers', 'trace'];
      const KNOWN_IDS = new Set([
        ...SYSTEM_PROMPT_IDS,
        ...TASK_IDS,
        ...MODEL_IDS,
        ...SKIP_IDS,
      ]);

      const findParam = (fieldIds: string[]) => {
        const p = struct.params!.find((param) =>
          fieldIds.includes(param.field_id),
        );
        return typeof p?.value === 'string' ? p.value : '';
      };

      const extraParams: Record<string, unknown> = {};
      for (const p of struct.params) {
        if (
          !KNOWN_IDS.has(p.field_id) &&
          !p.field_id.startsWith('_') &&
          p.value !== undefined &&
          p.value !== null &&
          p.value !== ''
        ) {
          extraParams[p.display_name || p.field_id] = p.value;
        }
      }

      const available_fields = [
        ...struct.params
          .filter(
            (p) =>
              !SKIP_IDS.includes(p.field_id) && !p.field_id.startsWith('_'),
          )
          .map((p) => p.field_id),
        'user_config_name',
        'user_config_description',
      ];

      const currentDescription = struct.user_config_description || '';

      return {
        node_model: findParam(MODEL_IDS),
        system_prompt: findParam(SYSTEM_PROMPT_IDS),
        task: findParam(TASK_IDS),
        node_display_name: getNodeDisplayName(nodeId) ?? nodeId,
        node_description: currentDescription,
        extra_params:
          Object.keys(extraParams).length > 0 ? extraParams : undefined,
        available_fields,
      };
    },
    [getNode, getNodeDisplayName],
  );

  const selectedNodeDisplayName = useNodeStore((state) => {
    if (!selectedNodeId) return null;
    const node = state.nodes.find((n) => n.id === selectedNodeId);
    return getStructDisplayLabel(node?.data?.struct) ?? selectedNodeId;
  });

  /** Spinner while waiting on the model; stays true for the full stream until awaiting_user_input. */
  const isGeneralChatInputBusy = useMemo(
    () =>
      isGeneralChatLoading ||
      (isGeneralChatActive && !isGeneralChatReady && !isGeneralChatConnecting),
    [
      isGeneralChatLoading,
      isGeneralChatActive,
      isGeneralChatReady,
      isGeneralChatConnecting,
    ],
  );

  // Debounced persist of messages to IndexedDB.
  const schedulePersist = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const wfId = effectiveWorkflowIdRef.current;
      if (!wfId) return;
      // Persist using the workflow key — single conversation thread
      const cacheMap = new Map<string, ChatMessageData[]>();
      cacheMap.set(WORKFLOW_WS_KEY, chatMessagesRef.current);
      saveChatHistory(wfId, cacheMap).catch((err) => {
        console.error('[chat-persist] save failed', err);
        Sentry.captureException(err, {
          extra: { context: 'general-chat-persist-save', workflowId: wfId },
        });
      });
    }, SAVE_DEBOUNCE_MS);
  }, []);

  // Apply a message update to chatMessages + schedule persistence.
  const applyMessage = useCallback(
    (updater: (prev: ChatMessageData[]) => ChatMessageData[]) => {
      commitChatMessageUpdate(chatMessagesRef, setChatMessages, updater);
      schedulePersist();
    },
    [schedulePersist],
  );

  // Build a simplified workflow graph payload for set_context.
  // Reads directly from useNodeStore to avoid stale/empty results from
  // useReactFlow().getNodes() which can lag behind after page refresh.
  const buildWorkflowContextPayload = useCallback(() => {
    const { nodes, edges } = useNodeStore.getState();

    const simplifiedNodes = nodes.map((node) => {
      const struct = (
        node.data as unknown as { struct?: Record<string, unknown> }
      )?.struct;
      const params: Record<string, unknown> = {};
      if (struct && Array.isArray(struct.params)) {
        for (const p of struct.params as Array<{
          field_id: string;
          value: unknown;
        }>) {
          if (p.value !== undefined && p.value !== null && p.value !== '') {
            params[p.field_id] = p.value;
          }
        }
      }
      return {
        id: node.id,
        node_type: (struct?.node_id as string) ?? '',
        display_name: (struct?.display_name as string) ?? '',
        user_config_name: (struct?.user_config_name as string) ?? '',
        user_config_description:
          (struct?.user_config_description as string) ?? '',
        position: node.position,
        params,
      };
    });

    const simplifiedEdges = edges.map((edge) => ({
      source: edge.source,
      target: edge.target,
      target_param:
        (edge.data as { target_param_id?: string } | undefined)
          ?.target_param_id ??
        (edge as Record<string, unknown>).target_param_id ??
        '',
    }));

    return {
      nodes: simplifiedNodes,
      edges: simplifiedEdges,
    };
  }, []);

  // Build a simplified node catalog for set_context.
  const buildNodeCatalogPayload = useCallback(() => {
    // Read directly from store to avoid stale closure issues
    const currentGroupedNodes = useNodeStore.getState().groupedNodes;
    const catalog: Array<{
      node_id: string;
      display_name: string;
      node_type: string;
      description: string;
      params: Array<{
        field_id: string;
        required: boolean;
      }>;
    }> = [];
    for (const [, structs] of Object.entries(currentGroupedNodes)) {
      if (!Array.isArray(structs)) continue;
      for (const s of structs) {
        catalog.push({
          node_id: s.node_id ?? '',
          display_name: s.display_name ?? '',
          node_type: s.node_type ?? '',
          description: s.description ?? '',
          params: Array.isArray(s.params)
            ? s.params
                .filter((p) => !p.hidden && !p.field_id.startsWith('_'))
                .map((p) => ({
                  field_id: p.field_id,
                  required: p.required,
                }))
            : [],
        });
      }
    }
    return catalog;
  }, []);

  // Track the latest combined editing lock in a ref so handlers and the
  // context builder always see the current value without needing to be in deps.
  // Covers both canvas padlock and workflow lock.
  const isEditingLockedRef = useRef(isEditingLocked);
  isEditingLockedRef.current = isEditingLocked;
  // Workflow lock is admin-imposed and takes precedence in lock messaging
  // (see isEditingLockedRef) since unlocking the canvas padlock alone
  // wouldn't resolve it.
  const isWorkflowLockActiveRef = useRef(isWorkflowLockActive);
  isWorkflowLockActiveRef.current = isWorkflowLockActive;

  const { applyBufferedMutations, coerceChatFieldValue } =
    useWorkflowMutationApplier({
      isEditingLocked,
      isWorkflowLockActive,
    });

  // Build the unified set_context payload (workflow graph + optional focused node).
  const buildUnifiedContextPayload = useCallback(
    (focusedNodeId: string | null) => {
      const { nodes, edges } = buildWorkflowContextPayload();
      const catalogPayload = buildNodeCatalogPayload();

      const payload: Record<string, unknown> = {
        message_type: 'set_context',
        workflow_id: effectiveWorkflowIdRef.current,
        workflow_name: '',
        workflow_description: '',
        nodes,
        edges,
        node_catalog: catalogPayload,
        // Tell the backend whether editing is locked (canvas padlock OR
        // workflow lock) so it can hide mutation tools and refuse tool calls.
        is_locked: isEditingLockedRef.current,
      };

      if (focusedNodeId) {
        const nodeContext = extractNodeContext(focusedNodeId);
        const node = getNode(focusedNodeId);
        const nodeData = node?.data as
          | { struct?: { node_type?: string } }
          | undefined;
        if (nodeContext) {
          payload.focused_node = {
            node_type: nodeData?.struct?.node_type ?? '',
            ...nodeContext,
          };
        }
      }

      return payload;
    },
    [
      buildWorkflowContextPayload,
      buildNodeCatalogPayload,
      extractNodeContext,
      getNode,
    ],
  );

  // Append a completed action to the workflow_progress message in the chat.
  // Creates the progress message on the first call; appends on subsequent calls.
  const appendWorkflowProgress = useCallback(
    (label: string) => {
      applyMessage((prev) => {
        const lastMsg = prev[prev.length - 1];
        if (
          lastMsg?.variant === 'workflow_progress' &&
          !lastMsg.progressComplete
        ) {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMsg,
              progressActions: [
                ...(lastMsg.progressActions ?? []),
                { label, completed: true },
              ],
            },
          ];
        }
        return [
          ...prev,
          {
            id: uuidv4(),
            role: 'assistant' as const,
            content: '',
            variant: 'workflow_progress' as const,
            progressActions: [{ label, completed: true }],
            progressComplete: false,
            timestamp: new Date(),
          },
        ];
      });
    },
    [applyMessage],
  );

  // Mark the current workflow_progress message as complete and flush any
  // buffered workflow mutations into a confirmation card.
  //
  // applyMessage updates chatMessagesRef synchronously, so callers that read it
  // immediately after this returns — e.g. applyPendingChatProposals scanning
  // for the just-flushed card — see the latest messages without racing React's
  // batched state updates.
  const finalizeWorkflowProgress = useCallback(() => {
    const buffered = pendingWorkflowMutationsRef.current;
    pendingWorkflowMutationsRef.current = [];
    pendingNodeNameMapRef.current.clear();
    const runRequested = pendingRunRequestRef.current;
    pendingRunRequestRef.current = false;

    const confirmCard: ChatMessageData | null =
      buffered.length > 0
        ? {
            id: uuidv4(),
            role: 'assistant' as const,
            content: `${buffered.length} workflow change${buffered.length !== 1 ? 's' : ''} ready to apply.`,
            variant: 'workflow_mutations_confirm' as const,
            pendingWorkflowMutations: buffered,
            workflowMutationsApplied: false,
            timestamp: new Date(),
          }
        : null;

    applyMessage((current) => {
      const updated = current.map((m) =>
        m.variant === 'workflow_progress' && !m.progressComplete
          ? { ...m, progressComplete: true }
          : m,
      );
      return confirmCard ? [...updated, confirmCard] : updated;
    });

    // Dispatch immediately — no confirmation click. If the editor
    // refuses (validation errors, run already in flight), its toasts
    // explain why; the user can simply ask to run again.
    if (runRequested) {
      dispatchChatRunRef.current(buffered.length > 0);
    }
  }, [applyMessage]);
  finalizeWorkflowProgressRef.current = finalizeWorkflowProgress;

  // Combined effect: handles workflow switching, history loading, and WS reconnect.
  //
  // Merging these into one effect eliminates the ordering race where the load
  // effect could check historyLoadedRef before the switch effect resets it.
  //
  // Cases handled:
  //   1. Initial mount (prev === effectiveWorkflowId): load history only
  //   2. Workflow switch (prev !== effectiveWorkflowId, both non-null): flush old, reset, load new, reconnect WS
  //   3. Draft saved (null → realId): migrate current messages to the new id, load any persisted history on top, reconnect WS
  //   4. effectiveWorkflowId is null: skip persistence entirely
  const prevWorkflowIdRef = useRef(effectiveWorkflowId);
  useEffect(() => {
    const prev = prevWorkflowIdRef.current;
    prevWorkflowIdRef.current = effectiveWorkflowId;
    const isInitialMount = prev === effectiveWorkflowId;
    const isWorkflowChange = !isInitialMount;

    if (isWorkflowChange) {
      // Flush old workflow's messages to IndexedDB before switching
      if (prev) {
        const cacheMap = new Map<string, ChatMessageData[]>();
        cacheMap.set(WORKFLOW_WS_KEY, chatMessagesRef.current);
        saveChatHistory(prev, cacheMap).catch((err) => {
          console.error('[chat-persist] flush on workflow switch failed', err);
          Sentry.captureException(err, {
            extra: {
              context: 'general-chat-persist-flush-switch',
              workflowId: prev,
            },
          });
        });
      }

      // Reset refs for the new workflow
      historyLoadedForRef.current = null;
      lastContextHashRef.current = '';
      lastFocusedNodeIdRef.current = null;
      pendingWorkflowMutationsRef.current = [];
      pendingNodeNameMapRef.current.clear();
      liveSessionStartIndexRef.current = 0;
      pendingRunRequestRef.current = false;

      // For null→realId (draft saved): keep existing messages (they were
      // never persisted since effectiveWorkflowId was null). For real→real
      // (workflow switch): reset to a fresh welcome message.
      if (prev !== null) {
        const welcomeMessages: ChatMessageData[] = [
          {
            id: uuidv4(),
            role: 'assistant' as const,
            content: `Hi, I'm **Operator**. I can help you build and edit your workflow — add nodes, connect them, configure parameters, or create an entire workflow from a description.\n\nWhen you select a node, I'll focus on helping you configure it. What would you like to do?`,
            timestamp: new Date(),
          },
        ];
        applyMessage(() => welcomeMessages);
      }

      // Disconnect old WS and open a fresh one for the new workflow
      const ws = workflowWsRef.current;
      if (ws) {
        ws.close();
        workflowWsRef.current = null;
      }
      setGeneralChatActive(false);
      setIsGeneralChatReady(false);
      // Read model from store at call time to avoid stale closure capture
      const currentModel = useChatUIStore.getState().selectedModel;
      connectWorkflowChat(currentModel);
    }

    // Load persisted history from IndexedDB
    if (
      !effectiveWorkflowId ||
      historyLoadedForRef.current === effectiveWorkflowId
    )
      return;
    let cancelled = false;

    loadChatHistory(effectiveWorkflowId)
      .then((cached) => {
        if (cancelled) return;
        historyLoadedForRef.current = effectiveWorkflowId;
        // Only this workflow's first load this visit can be a prior session;
        // capture that before recording it as hydrated for every load after.
        const isFirstHydration =
          !hydratedWorkflowIdsRef.current.has(effectiveWorkflowId);
        hydratedWorkflowIdsRef.current.add(effectiveWorkflowId);
        if (!cached || cached.size === 0) return;
        const restored = cached.get(WORKFLOW_WS_KEY);
        if (restored && restored.length > 0) {
          const sanitizedRestored = sanitizeStalePendingClarifications(
            restored,
            runSessionIdRef.current,
          );
          liveSessionStartIndexRef.current = isFirstHydration
            ? sanitizedRestored.length
            : 0;
          // The IDB load is async, so a WS event may already have appended a
          // card; replacing outright would wipe it before it persists.
          // Composes off chatMessagesRef.current (via applyMessage) rather
          // than React's own state so it can't lose a card that a batched WS
          // frame wrote to the ref in the meantime.
          applyMessage((current) => {
            // Empty or welcome-only current state: nothing to
            // preserve, restore the cache wholesale.
            if (current.length <= 1) {
              return sanitizedRestored;
            }
            const restoredIds = new Set(sanitizedRestored.map((m) => m.id));
            const inFlightAdditions = current.filter(
              (m) => !restoredIds.has(m.id),
            );
            return [...sanitizedRestored, ...inFlightAdditions];
          });
        }
      })
      .catch((err) => {
        if (!cancelled) historyLoadedForRef.current = effectiveWorkflowId;
        console.error('[chat-persist] load failed', err);
        Sentry.captureException(err, {
          extra: {
            context: 'general-chat-persist-load',
            workflowId: effectiveWorkflowId,
          },
        });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveWorkflowId]);

  // Resolve a node's display name, checking the pending name map first
  // (for nodes created in the current buffered batch that aren't on canvas yet).
  const resolveNodeName = useCallback(
    (instanceId: string): string => {
      return (
        pendingNodeNameMapRef.current.get(instanceId) ??
        getNodeDisplayName(instanceId) ??
        instanceId
      );
    },
    [getNodeDisplayName],
  );

  // Build a human-readable label for a workflow mutation event.
  const buildMutationLabel = useCallback(
    (data: Record<string, unknown>): string =>
      buildSharedMutationLabel(data, resolveNodeName),
    [resolveNodeName],
  );

  // Buffer workflow mutation events instead of executing immediately.
  // Events are collected and shown in a confirmation card after the LLM turn.
  const handleWorkflowMutationEvent = useCallback(
    (data: Record<string, unknown>) => {
      const eventType = data.event_type as string;

      // Track display names from add_node so later events can reference them
      if (eventType === 'add_node') {
        const instanceId = data.instance_id as string;
        const configName = data.user_config_name as string | undefined;
        const nodeType = data.node_type as string;
        const currentGroupedNodes = useNodeStore.getState().groupedNodes;
        const nodeStruct = findCatalogStruct(currentGroupedNodes, nodeType);
        const displayName = configName || nodeStruct?.display_name || nodeType;
        if (instanceId) {
          pendingNodeNameMapRef.current.set(instanceId, displayName);
        }
      }

      const label = buildMutationLabel(data);
      pendingWorkflowMutationsRef.current.push({ label, eventData: data });
      appendWorkflowProgress(label);
    },
    [buildMutationLabel, appendWorkflowProgress],
  );
  handleWorkflowMutationEventRef.current = handleWorkflowMutationEvent;

  // Connect the single unified chat WebSocket.
  const connectWorkflowChat = useCallback(
    async (model: string) => {
      const existing = workflowWsRef.current;
      if (existing && existing.readyState === WebSocket.OPEN) {
        setIsGeneralChatConnecting(false);
        setGeneralChatActive(true);
        setIsGeneralChatReady(true);
        return;
      }
      if (existing) {
        existing.close();
        workflowWsRef.current = null;
        lastContextHashRef.current = '';
      }

      const gen = ++generalChatGenRef.current;

      setIsGeneralChatConnecting(true);
      setIsGeneralChatReady(false);

      try {
        const session = await createGeneralChatSession({
          nodeType: '',
          nodeName: 'Workflow',
          model,
          orgId,
        });

        if (gen !== generalChatGenRef.current) return;

        if (!session?.chat_socket_url) {
          setIsGeneralChatConnecting(false);
          return;
        }

        const token = await getFreshToken();

        if (gen !== generalChatGenRef.current) return;

        const url = new URL(session.chat_socket_url);
        if (token) url.searchParams.set('token', token);

        const ws = new WebSocket(url.toString());
        workflowWsRef.current = ws;

        ws.onopen = () => {
          setIsGeneralChatConnecting(false);
          setGeneralChatActive(true);

          // Read from ref to get the latest selection, not the stale closure value
          const contextPayload = buildUnifiedContextPayload(
            selectedNodeIdRef.current,
          );
          ws.send(JSON.stringify(contextPayload));

          // Replay recent conversation history so the LLM retains context.
          // Only send the last N messages — older messages may describe a
          // different workflow state and cause the LLM to hallucinate stale
          // node counts, names, or configs. The system prompt's LIVE
          // workflow state is the source of truth; history is just for
          // conversational continuity.
          const HISTORY_REPLAY_WINDOW = 20;
          const currentMessages = chatMessagesRef.current;
          if (currentMessages && currentMessages.length > 0) {
            const replayable = currentMessages
              .filter(
                (m) =>
                  (m.role === 'user' || m.role === 'assistant') &&
                  m.content &&
                  m.variant !== 'thinking' &&
                  m.variant !== 'divider' &&
                  m.variant !== 'node_update' &&
                  m.variant !== 'remove_node_confirm' &&
                  m.variant !== 'workflow_mutations_confirm' &&
                  m.variant !== 'workflow_progress' &&
                  // Not user/assistant turns: replaying them as role/content
                  // pairs would corrupt history. The BE gets them via deliver.
                  m.variant !== 'clarification_request',
              )
              .slice(-HISTORY_REPLAY_WINDOW)
              .map((m) => ({ role: m.role, content: m.content }));
            if (replayable.length > 0) {
              ws.send(
                JSON.stringify({
                  message_type: 'set_history',
                  messages: replayable,
                }),
              );
            }
          }

          // Pushed on init so the agent's first-turn system prompt picks the
          // right doctrine. Read from the store directly to dodge a stale
          // closure; the effect below handles mid-session toggles.
          const initPrefs = useClarificationPrefsStore.getState();
          const initAllowClarifications = modeAllowsClarifications(
            initPrefs.workflowSession?.mode ?? initPrefs.mode,
          );
          ws.send(
            JSON.stringify({
              message_type: 'update_clarification_prefs',
              allow_clarifications: initAllowClarifications,
            }),
          );
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.event_type === 'chat_connected') {
              setIsGeneralChatReady(true);
              return;
            }

            if (data.event_type === 'awaiting_user_input') {
              setIsGeneralChatLoading(false);
              setIsGeneralChatReady(true);
              finalizeWorkflowProgressRef.current();
              return;
            }

            if (data.event_type === 'chat_disconnected') {
              setGeneralChatActive(false);
              setIsGeneralChatReady(false);
              // Don't auto-run on a dead socket; still flush the Apply card.
              pendingRunRequestRef.current = false;
              finalizeWorkflowProgressRef.current();
              return;
            }

            // Handle workflow mutation events (use ref to avoid stale closure)
            if (
              [
                'add_node',
                'remove_node',
                'configure_node',
                'connect_nodes',
                'disconnect_nodes',
              ].includes(data.event_type)
            ) {
              handleWorkflowMutationEventRef.current(data);
              return;
            }

            // Buffered; a run card is appended and the run
            // auto-dispatches when the turn finalizes.
            if (data.event_type === 'run_workflow') {
              pendingRunRequestRef.current = true;
              return;
            }

            if (data.event_type === 'node_update') {
              const updates = data.updates as
                | Array<{ field_id: string; value: string }>
                | undefined;
              if (updates && updates.length > 0) {
                const count = updates.length;
                const summaryText = `Suggested ${count} config change${count !== 1 ? 's' : ''}.`;
                applyMessage((prev) => [
                  ...prev,
                  {
                    id: uuidv4(),
                    role: 'assistant' as const,
                    content: summaryText,
                    variant: 'node_update' as const,
                    pendingUpdates: updates,
                    targetNodeId: selectedNodeIdRef.current ?? undefined,
                    updatesApplied: false,
                    timestamp: new Date(),
                  },
                ]);
              }
              return;
            }

            const isAgentMsg = data.display_type === 'chat_agent_message';
            const isThinking = data.display_type === 'thinking';

            if (isAgentMsg || isThinking) {
              const newContent =
                typeof data.data === 'string'
                  ? data.data
                  : (data.message ?? '');
              const messageId = data.block_id || data.event_id || uuidv4();
              const isStreaming = data.mode === 'textstream';
              const variant = isThinking ? 'thinking' : 'default';

              const streamMap = nodeStreamingRef.current;
              const currentStreaming = streamMap.get(WORKFLOW_WS_KEY) ?? null;

              if (
                isStreaming &&
                currentStreaming !== null &&
                currentStreaming.id === messageId
              ) {
                const accumulated = accumulateStreamingText(
                  currentStreaming.content,
                  newContent,
                );
                currentStreaming.content = accumulated;
                applyMessage((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId
                      ? { ...msg, content: accumulated, variant }
                      : msg,
                  ),
                );
              } else if (isStreaming) {
                streamMap.set(WORKFLOW_WS_KEY, {
                  id: messageId,
                  content: newContent,
                });
                applyMessage((prev) => [
                  ...prev,
                  {
                    id: messageId,
                    role: 'assistant' as const,
                    content: newContent,
                    timestamp: new Date(),
                    variant,
                  },
                ]);
              } else {
                streamMap.delete(WORKFLOW_WS_KEY);
                applyMessage((prev) => [
                  ...prev,
                  {
                    id: messageId,
                    role: 'assistant' as const,
                    content: newContent,
                    timestamp: new Date(),
                    variant,
                  },
                ]);
              }
            }
          } catch (err) {
            console.error('Failed to parse general chat message:', err);
            Sentry.captureException(err, {
              extra: { context: 'general-chat-ws-onmessage' },
            });
          }
        };

        ws.onclose = () => {
          workflowWsRef.current = null;
          lastContextHashRef.current = '';
          pendingRunRequestRef.current = false;
          finalizeWorkflowProgressRef.current();
          setGeneralChatActive(false);
          setIsGeneralChatReady(false);
          setIsGeneralChatConnecting(false);
        };

        ws.onerror = (error) => {
          console.error('General chat WebSocket error:', error);
          Sentry.captureException(error, {
            extra: { context: 'general-chat-ws-onerror' },
          });
          setIsGeneralChatConnecting(false);
        };
      } catch (error) {
        console.error('Failed to create general chat session:', error);
        Sentry.captureException(error, {
          extra: { context: 'create-general-chat-session' },
        });
        setIsGeneralChatConnecting(false);
      }
    },
    [
      setGeneralChatActive,
      applyMessage,
      orgId,
      buildUnifiedContextPayload,
      selectedNodeId,
    ],
  );

  const disconnectWorkflowChat = useCallback(() => {
    const ws = workflowWsRef.current;
    if (ws) {
      ws.close();
      workflowWsRef.current = null;
    }
    lastContextHashRef.current = '';
    setGeneralChatActive(false);
    setIsGeneralChatReady(false);
  }, [setGeneralChatActive]);

  // Send updated set_context to the existing WS (no reconnection needed).
  const sendContextUpdate = useCallback(
    (focusedNodeId: string | null) => {
      const ws = workflowWsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      const contextPayload = buildUnifiedContextPayload(focusedNodeId);
      const digest = hashGeneralChatSetContextPayload(
        contextPayload as unknown as Record<string, unknown>,
      );

      if (lastContextHashRef.current !== digest) {
        ws.send(JSON.stringify(contextPayload));
        lastContextHashRef.current = digest;
      }
    },
    [buildUnifiedContextPayload],
  );

  // On mount: show welcome message and connect the unified WS.
  // On node selection change: insert focus divider and send context update.
  useEffect(() => {
    // Initialize welcome message on first render
    if (chatMessages.length === 0) {
      const welcomeMessages: ChatMessageData[] = [
        {
          id: uuidv4(),
          role: 'assistant' as const,
          content: `Hi, I'm **Operator**. I can help you build and edit your workflow — add nodes, connect them, configure parameters, or create an entire workflow from a description.\n\nWhen you select a node, I'll focus on helping you configure it. What would you like to do?`,
          timestamp: new Date(),
        },
      ];
      applyMessage(() => welcomeMessages);
    }

    setChatMode('workflow');

    // Connect WS if not already connected
    const ws = workflowWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      connectWorkflowChat(selectedModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When node selection changes, send updated context (no reconnect).
  useEffect(() => {
    const prevFocused = lastFocusedNodeIdRef.current;
    lastFocusedNodeIdRef.current = selectedNodeId;

    // Insert a subtle focus divider if the selection changed.
    // Skip the very first render so we don't insert a divider on mount.
    if (prevFocused !== selectedNodeId && focusDividerInitializedRef.current) {
      if (selectedNodeId) {
        const nodeLabel = getNodeDisplayName(selectedNodeId) ?? 'this node';
        applyMessage((prev) => [
          ...prev,
          {
            id: uuidv4(),
            role: 'assistant' as const,
            content: `Now focused on **${nodeLabel}**`,
            timestamp: new Date(),
            variant: 'divider' as const,
          },
        ]);
      } else {
        applyMessage((prev) => [
          ...prev,
          {
            id: uuidv4(),
            role: 'assistant' as const,
            content: `Back to **workflow view**`,
            timestamp: new Date(),
            variant: 'divider' as const,
          },
        ]);
      }
    }

    focusDividerInitializedRef.current = true;

    // Send context update to existing WS
    sendContextUpdate(selectedNodeId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId]);

  // Re-send context when the store transitions from empty to populated.
  // On page refresh, the WS connects before nodes load, so the initial
  // set_context has an empty graph. This catches the 0→N transition.
  // For all other cases, sendGeneralChatMessage already sends fresh
  // set_context before every user message, so the LLM always has the
  // latest state when it matters most (right before responding).
  const storeNodeCount = useNodeStore((s) => s.nodes.length);
  const prevNodeCountRef = useRef(storeNodeCount);
  useEffect(() => {
    const wasEmpty = prevNodeCountRef.current === 0;
    prevNodeCountRef.current = storeNodeCount;

    if (wasEmpty && storeNodeCount > 0) {
      sendContextUpdate(selectedNodeId);
    }
  }, [storeNodeCount, sendContextUpdate, selectedNodeId]);

  // Handle model changes without reconnecting the WS
  const prevModelRef = useRef(selectedModel);
  useEffect(() => {
    if (prevModelRef.current === selectedModel) return;
    prevModelRef.current = selectedModel;

    const ws = workflowWsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({ message_type: 'set_model', model: selectedModel }),
      );

      const modelLabel =
        GENERAL_CHAT_MODELS.find((m) => m.id === selectedModel)?.label ??
        selectedModel;
      const modelDivider: ChatMessageData = {
        id: uuidv4(),
        role: 'assistant' as const,
        content: `Switched to **${modelLabel}**`,
        timestamp: new Date(),
        variant: 'divider' as const,
      };
      applyMessage((prev) => [...prev, modelDivider]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel]);

  // Skips the first run, already pushed in ws.onopen. No divider: it's a
  // quiet preference change.
  const prevAllowClarificationsRef = useRef(allowClarifications);
  useEffect(() => {
    if (prevAllowClarificationsRef.current === allowClarifications) return;
    prevAllowClarificationsRef.current = allowClarifications;

    const ws = workflowWsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          message_type: 'update_clarification_prefs',
          allow_clarifications: allowClarifications,
        }),
      );
    }
  }, [allowClarifications]);

  // Re-send set_context when the lock state flips so the BE can swap
  // tool sets immediately, and insert a divider to surface it.  Only
  // fires for an in-place toggle on the SAME workflow -- navigation or
  // initial hydration (false → true) must not show a spurious divider
  // (gated by isLockStateHydrated below).
  // Tracks the combined editing lock (canvas padlock OR workflow lock) so a
  // padlock toggle also refreshes the backend context.
  const prevLockedRef = useRef<boolean | null>(null);
  const prevLockedWorkflowIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Skip until the store's currentWorkflow has caught up to the URL's
    // workflowId. Otherwise the value of `isEditingLocked` is from the
    // wrong workflow (or defaulted to false because the store is empty)
    // and we'd fire spurious dividers / context updates.
    if (!isLockStateHydrated) return;

    const previousLocked = prevLockedRef.current;
    const previousWorkflowId = prevLockedWorkflowIdRef.current;
    const currentWorkflowId = effectiveWorkflowId;

    prevLockedRef.current = isEditingLocked;
    prevLockedWorkflowIdRef.current = currentWorkflowId;

    if (previousLocked === null) return;
    if (previousWorkflowId !== currentWorkflowId) return;
    if (previousLocked === isEditingLocked) return;

    sendContextUpdate(selectedNodeIdRef.current);

    const dividerContent = isEditingLocked
      ? isWorkflowLockActive
        ? "🔒 Workflow is now **locked** by an admin. I can still answer questions and debug runs, but I won't propose edits until it's unlocked."
        : "🔒 Canvas editing is now **locked**. I can still answer questions and debug runs, but I won't propose edits until you unlock the canvas."
      : '🔓 Editing is **unlocked**. I can propose edits again.';
    applyMessage((prev) => [
      ...prev,
      {
        id: uuidv4(),
        role: 'assistant' as const,
        content: dividerContent,
        timestamp: new Date(),
        variant: 'divider' as const,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingLocked, effectiveWorkflowId, isLockStateHydrated]);

  // Flush chat history to IndexedDB immediately (bypasses debounce).
  // Used by both unmount cleanup and beforeunload to avoid data loss.
  const flushPersist = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    const wfId = effectiveWorkflowIdRef.current;
    if (!wfId) return;
    const cacheMap = new Map<string, ChatMessageData[]>();
    cacheMap.set(WORKFLOW_WS_KEY, chatMessagesRef.current);
    saveChatHistory(wfId, cacheMap).catch((err) => {
      console.error('[chat-persist] flush on unmount failed', err);
      Sentry.captureException(err, {
        extra: {
          context: 'general-chat-persist-flush-unmount',
          workflowId: wfId,
        },
      });
    });
  }, []);

  // Persist on page refresh / tab close. The browser lets in-flight
  // IndexedDB transactions complete during beforeunload, so kicking
  // off the write here is enough to survive a hard refresh mid-stream.
  useEffect(() => {
    const handleBeforeUnload = () => flushPersist();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [flushPersist]);

  // Cleanup on unmount: flush pending save and disconnect WS.
  useEffect(() => {
    return () => {
      flushPersist();
      disconnectWorkflowChat();
    };
  }, [flushPersist, disconnectWorkflowChat]);

  // Send a general chat message (with context dedup).
  // Returns true if the message was sent, false if the WS was unavailable.
  const sendGeneralChatMessage = useCallback(
    (message: string): boolean => {
      const ws = workflowWsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return false;
      }

      setIsGeneralChatLoading(true);
      setIsGeneralChatReady(false);

      // Always send fresh context before a message so the LLM sees
      // the latest graph state (bypass dedup hash).
      const contextPayload = buildUnifiedContextPayload(selectedNodeId);
      const contextNodes = (contextPayload as Record<string, unknown>).nodes;
      console.info(
        '[chat] Sending context before message: %d nodes, %d edges',
        Array.isArray(contextNodes) ? contextNodes.length : 0,
        Array.isArray((contextPayload as Record<string, unknown>).edges)
          ? ((contextPayload as Record<string, unknown>).edges as unknown[])
              .length
          : 0,
      );
      ws.send(JSON.stringify(contextPayload));
      lastContextHashRef.current = hashGeneralChatSetContextPayload(
        contextPayload as unknown as Record<string, unknown>,
      );

      ws.send(
        JSON.stringify({
          message_type: 'chat_message',
          message,
        }),
      );
      return true;
    },
    [buildUnifiedContextPayload, selectedNodeId],
  );

  const handleApplyUpdates = useCallback(
    (messageId: string): boolean => {
      const allMsgs = chatMessagesRef.current;
      const msg = allMsgs.find((m) => m.id === messageId);
      if (!msg?.pendingUpdates || msg.updatesApplied) return false;

      // Defense-in-depth: the Apply button is already disabled when editing
      // is locked, but guard the handler too so a stale UI state, race with
      // the lock toggle, or programmatic invocation can't slip a mutation
      // through.  Covers both canvas padlock and workflow lock.
      if (isEditingLockedRef.current) {
        notification.warning(
          getEditingLockedWarning(isWorkflowLockActiveRef.current),
        );
        return false;
      }

      // Use the node ID captured when the suggestion was created, not the
      // current selection — the user may have switched nodes since then.
      const targetNodeId = msg.targetNodeId ?? selectedNodeId;
      if (!targetNodeId) return false;

      for (const { field_id, value } of msg.pendingUpdates) {
        if (field_id === 'user_config_name') {
          updateNodeAttributes(targetNodeId, { user_config_name: value });
        } else if (field_id === 'user_config_description') {
          updateNodeAttributes(targetNodeId, {
            user_config_description: value,
          });
        } else {
          handleParameterChange(
            targetNodeId,
            field_id,
            coerceChatFieldValue(targetNodeId, field_id, value),
          );
        }
      }

      notification.success('Node settings have been updated!');
      setConsoleSidebarActiveTab('configure');
      setConsolePanelOpen(true);

      applyMessage((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, updatesApplied: true } : m,
        ),
      );
      return true;
    },
    [
      selectedNodeId,
      handleParameterChange,
      updateNodeAttributes,
      notification,
      setConsoleSidebarActiveTab,
      setConsolePanelOpen,
      applyMessage,
      coerceChatFieldValue,
    ],
  );

  // Apply all buffered workflow mutations from a confirmation card.
  const handleApplyWorkflowMutations = useCallback(
    (messageId: string): boolean => {
      const allMsgs = chatMessagesRef.current;
      const msg = allMsgs.find((m) => m.id === messageId);
      if (!msg?.pendingWorkflowMutations || msg.workflowMutationsApplied)
        return false;

      if (!applyBufferedMutations(msg.pendingWorkflowMutations, messageId))
        return false;

      applyMessage((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, workflowMutationsApplied: true } : m,
        ),
      );
      return true;
    },
    [applyBufferedMutations, applyMessage],
  );

  // Applies pending confirm/node-update cards for isApplyChangesIntent.
  // 'none-pending' means there was nothing to apply; 'attempt-failed' means a
  // card was there but every attempt was refused (editing lock, missing
  // target node, or the mutation apply itself failing) — each of those paths
  // already surfaces its own toast, so callers shouldn't add a generic one.
  const applyPendingChatProposals = useCallback(():
    | 'applied'
    | 'none-pending'
    | 'attempt-failed' => {
    finalizeWorkflowProgress();

    // Scope to this page load's live activity rather than the whole
    // persisted transcript — a confirm/node-update card the user
    // deliberately left unapplied in an earlier, restored session shouldn't
    // get silently applied by a later, unrelated "apply changes".
    const allMsgs = chatMessagesRef.current;
    const liveStart = liveSessionStartIndexRef.current;

    const idsToApply: string[] = [];
    for (let i = liveStart; i < allMsgs.length; i++) {
      const m = allMsgs[i];
      if (m.workflowMutationsApplied || m.updatesApplied) continue;
      if (
        m.variant === 'workflow_mutations_confirm' &&
        (m.pendingWorkflowMutations?.length ?? 0) > 0
      ) {
        idsToApply.push(m.id);
      } else if (
        m.variant === 'node_update' &&
        (m.pendingUpdates?.length ?? 0) > 0
      ) {
        idsToApply.push(m.id);
      }
    }
    if (idsToApply.length === 0) return 'none-pending';

    let appliedAny = false;
    for (const id of idsToApply) {
      const msg = chatMessagesRef.current.find((m) => m.id === id);
      const applied =
        msg?.variant === 'node_update'
          ? handleApplyUpdates(id)
          : handleApplyWorkflowMutations(id);
      appliedAny = appliedAny || applied;
    }
    return appliedAny ? 'applied' : 'attempt-failed';
  }, [
    finalizeWorkflowProgress,
    handleApplyUpdates,
    handleApplyWorkflowMutations,
  ]);

  // Insert a divider message into the conversation
  const insertDivider = useCallback(
    (content: string) => {
      const dividerMessage: ChatMessageData = {
        id: uuidv4(),
        role: 'assistant',
        content,
        timestamp: new Date(),
        variant: 'divider',
      };
      applyMessage((prev) => [...prev, dividerMessage]);
    },
    [applyMessage],
  );

  // Auto-dispatches when the assistant's turn finalizes. The editor
  // callback owns the guards (already-running, validation) and surfaces
  // its own notifications; the divider only lands on a real dispatch.
  const runDispatchInFlightRef = useRef(false);
  const dispatchChatRun = useCallback(
    async (sameTurnMutations = false) => {
      const onRunWorkflow = onRunWorkflowRef.current;
      if (!onRunWorkflow) return;
      // Serialize dispatches — an auto-dispatch racing another (e.g. two
      // finalizes back-to-back) must not start two runs. Surface feedback
      // so the second request isn't dropped silently.
      if (runDispatchInFlightRef.current) {
        notification.info(
          'A run is already starting — follow its progress on the canvas.',
        );
        return;
      }
      runDispatchInFlightRef.current = true;

      const hasUnappliedMutations =
        sameTurnMutations ||
        chatMessagesRef.current.some(
          (m) =>
            (m.variant === 'workflow_mutations_confirm' &&
              !m.workflowMutationsApplied &&
              (m.pendingWorkflowMutations?.length ?? 0) > 0) ||
            (m.variant === 'node_update' &&
              !m.updatesApplied &&
              (m.pendingUpdates?.length ?? 0) > 0),
        );
      if (hasUnappliedMutations) {
        notification.warning(
          'This run starts without your unapplied chat changes — apply them and run again to include them.',
        );
      }

      try {
        const started = await onRunWorkflow();
        if (!started) return;
        insertDivider('Run started — follow progress on the canvas');
      } finally {
        runDispatchInFlightRef.current = false;
      }
    },
    [notification, insertDivider],
  );
  dispatchChatRunRef.current = dispatchChatRun;

  // Reconnect if the WS is dead
  const ensureConnection = useCallback(() => {
    const ws = workflowWsRef.current;
    if (
      !ws ||
      ws.readyState === WebSocket.CLOSED ||
      ws.readyState === WebSocket.CLOSING
    ) {
      connectWorkflowChat(selectedModel);
    }
  }, [selectedModel, connectWorkflowChat]);

  // Confirm a batched node removal from the chat UI
  const handleConfirmRemoveNode = useCallback(
    (messageId: string) => {
      const allMsgs = chatMessagesRef.current;
      const msg = allMsgs.find((m) => m.id === messageId);
      const nodes = msg?.pendingRemoveNodes;
      if (!nodes || nodes.length === 0 || msg?.removalsConfirmed) return;

      // Defense-in-depth — see handleApplyUpdates for full rationale.
      // Covers both canvas padlock and workflow lock.
      if (isEditingLockedRef.current) {
        notification.warning(
          getEditingLockedWarning(isWorkflowLockActiveRef.current),
        );
        return;
      }

      saveSnapshot();
      withSuppressedSnapshots(() => {
        for (const { instanceId } of nodes) {
          onNodesEdgesDelete({ nodeId: instanceId });
        }
      });
      notification.success(
        nodes.length === 1
          ? `Removed ${nodes[0].nodeName}`
          : `Removed ${nodes.length} nodes`,
      );

      applyMessage((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, removalsConfirmed: true } : m,
        ),
      );
    },
    [saveSnapshot, onNodesEdgesDelete, notification, applyMessage],
  );

  // Poll for promoted artifacts when a chat-initiated run ends. Signals are
  // queued and drained one at a time: if a second chat run ends while the
  // first is still polling, the newer signal waits its turn instead of
  // cancelling or clobbering the one in flight.
  const chatRunQueueRef = useRef<ChatRunEndedSignal[]>([]);
  const lastEnqueuedChatRunSeqRef = useRef<number | null>(null);
  const isProcessingChatRunRef = useRef(false);
  const chatRunHookUnmountedRef = useRef(false);
  const onChatRunSignalConsumedRef = useRef(options?.onChatRunSignalConsumed);
  onChatRunSignalConsumedRef.current = options?.onChatRunSignalConsumed;
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;
  // Updated synchronously during render (not in an effect) so an in-flight
  // poll's staleness check can't race a workflow switch: a useEffect on
  // workflowId wouldn't flush until after render commits, leaving a window
  // where a resolved poll could still see the old workflow as "current" and
  // insert artifact cards into the newly opened workflow's chat.
  const currentWorkflowIdRef = useRef(workflowId);
  currentWorkflowIdRef.current = workflowId;

  useEffect(() => {
    chatRunQueueRef.current = [];
    isProcessingChatRunRef.current = false;
    lastEnqueuedChatRunSeqRef.current = null;
  }, [workflowId]);

  const processChatRunQueue = useCallback(async () => {
    if (isProcessingChatRunRef.current) return;
    const signal = chatRunQueueRef.current[0];
    if (!signal) return;
    isProcessingChatRunRef.current = true;
    const workflowIdAtStart = currentWorkflowIdRef.current;
    const isStale = () =>
      chatRunHookUnmountedRef.current ||
      currentWorkflowIdRef.current !== workflowIdAtStart;

    const { seq, runId, fallbackFiles } = signal;
    const finish = () => {
      onChatRunSignalConsumedRef.current?.(seq);
      chatRunQueueRef.current.shift();
      isProcessingChatRunRef.current = false;
      void processChatRunQueue();
    };
    const showFallback = () => {
      if (fallbackFiles.length === 0) return;
      const msg: ChatMessageData = {
        id: uuidv4(),
        role: 'assistant',
        content: '',
        variant: 'artifacts',
        fallbackFiles,
      };
      applyMessage((prev) => [...prev, msg]);
    };

    if (!runId) {
      // Run failed before a WorkflowRun row was created — show fallback
      showFallback();
      finish();
      return;
    }

    const POLL_INTERVALS_MS = [1000, 2000, 3000, 4000, 5000, 5000, 5000, 5000];
    for (let attempt = 0; attempt < POLL_INTERVALS_MS.length; attempt++) {
      await new Promise((resolve) =>
        setTimeout(resolve, POLL_INTERVALS_MS[attempt]),
      );
      if (isStale()) return;
      const currentOrgId = orgIdRef.current;
      if (!currentOrgId) continue;
      try {
        const resp = await listArtifacts(currentOrgId, {
          source_run_id: runId,
          limit: 50,
        });
        if (isStale()) return;
        const artifacts: ArtifactListItem[] = resp.artifacts ?? [];
        if (artifacts.length > 0) {
          const msg: ChatMessageData = {
            id: uuidv4(),
            role: 'assistant',
            content: '',
            variant: 'artifacts',
            artifacts,
          };
          applyMessage((prev) => [...prev, msg]);
          finish();
          return;
        }
      } catch (error) {
        if (attempt === POLL_INTERVALS_MS.length - 1) {
          Sentry.captureException(error, {
            extra: { context: 'poll-promoted-artifacts', runId },
          });
        }
      }
    }
    if (isStale()) return;
    showFallback();
    finish();
  }, [applyMessage]);

  useEffect(() => {
    const signal = options?.chatRunEndedSignal;
    if (!signal) return;
    if (lastEnqueuedChatRunSeqRef.current === signal.seq) return;
    lastEnqueuedChatRunSeqRef.current = signal.seq;
    chatRunQueueRef.current.push(signal);
    void processChatRunQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.chatRunEndedSignal?.seq]);

  useEffect(() => {
    return () => {
      chatRunHookUnmountedRef.current = true;
    };
  }, []);

  return {
    chatMessages,
    isGeneralChatConnecting,
    isGeneralChatLoading,
    isGeneralChatReady,
    isGeneralChatInputBusy,
    selectedNodeDisplayName,
    getNodeDisplayName,
    extractNodeContext,
    sendGeneralChatMessage,
    handleApplyUpdates,
    handleApplyWorkflowMutations,
    applyBufferedMutations,
    applyPendingChatProposals,
    handleConfirmRemoveNode,
    applyMessage,
    insertDivider,
    ensureConnection,
    // Surfaced to consumers so they can disable Apply buttons on
    // proposal cards when editing is locked. The backend already hides
    // mutation tools and refuses any hallucinated tool calls, but disabling
    // the Apply button is the final UI safeguard so a pre-existing proposal
    // card from before the lock can't be applied.
    isEditingLocked,
  };
}
